import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Wrench,
  LogOut,
  Calendar,
  CheckCircle,
  Clock,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Navigation,
  Phone,
  MapPin,
  AlertCircle,
  RotateCcw,
  Package,
  Menu,
  X,
  Briefcase,
  History,
} from "lucide-react";
import { Modal, DarkModeToggle } from "./ui";
import { PlanningCalendar } from "./PlanningCalendar";
import { DossierNotes } from "./DossierNotes";
import { SignedQuoteViewer } from "./commercial/SignedQuoteViewer";
import { JobsFiltersBar, useJobsFilters, STATUS_LABELS } from "./JobsFiltersBar";
import { JobsCategoryHub } from "./JobsCategoryHub";
import { JobsDossierTypeHub } from "./JobsDossierTypeHub";
import {
  categoryRequiresDossierType,
  DOSSIER_TYPE_FILTER_LABELS,
  JOB_CATEGORY_LABELS,
  jobMatchesCategory,
} from "../utils/jobCategories";
import type { DossierTypeFilter, JobCategory } from "../utils/jobCategories";
import { getDossierTypeLabel, getLightingTypeLabel } from "../utils/dossierLabels";
import type { Job, StockItem, User } from "../types";
import { getCurrentLocale } from "../i18n";

interface InstallerAppProps {
  jobs: Job[];
  currentUser: User;
  onValidateJob: (
    jobId: string,
    usageNeuf: Record<string, number>,
    usageRemplacement: Record<string, number>,
    notes: string
  ) => Promise<void>;
  onModifyJob: (
    jobId: string,
    usageNeuf: Record<string, number>,
    usageRemplacement: Record<string, number>,
    notes: string
  ) => Promise<void>;
  onUpdateJob: (job: Job) => void;
  onLogout: () => void;
  installerName: string;
  stock: StockItem[];
  /** Si true (défaut), afficher le partenaire qui a apporté le chantier. */
  installerCanSeePartner?: boolean;
  /** Liste des partenaires (pour résoudre le nom à partir de job.partnerId). */
  partners?: User[];
  /** Nom de la société de l'admin (affiché comme branding pour le poseur). */
  ownerCompanyName?: string;
  /** Logo / avatar de l'admin. */
  ownerLogoUrl?: string | null;
}

export function InstallerApp({
  jobs,
  currentUser,
  onValidateJob,
  onModifyJob,
  onUpdateJob,
  onLogout,
  installerName,
  stock,
  installerCanSeePartner = true,
  partners = [],
  ownerCompanyName = "",
  ownerLogoUrl,
}: InstallerAppProps) {
  const [view, setView] = useState<"dossiers" | "planning" | "history">("dossiers");
  const [dossiersScreen, setDossiersScreen] = useState<"hub" | "types" | "list">("hub");
  const [activeCategory, setActiveCategory] = useState<JobCategory | null>(null);
  const [activeDossierType, setActiveDossierType] = useState<DossierTypeFilter | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [detailJob, setDetailJob] = useState<Job | null>(null);
  const [validatingJob, setValidatingJob] = useState<Job | null>(null);
  const [editingScheduleJob, setEditingScheduleJob] = useState<Job | null>(null);
  const [editDate, setEditDate] = useState("");
  const [editTime, setEditTime] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [usageNeuf, setUsageNeuf] = useState<Record<string, number>>({});
  const [usageRemplacement, setUsageRemplacement] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [partnerSectionOpen, setPartnerSectionOpen] = useState(true);
  const [showValidationSuccess, setShowValidationSuccess] = useState(false);

  // Stock du partenaire du chantier dans la région du chantier uniquement (déduction au bon partenaire / région)
  const stockForJob = useMemo(
    () =>
      validatingJob
        ? stock.filter(
            (item) =>
              item.regionId === validatingJob.stockRegionId &&
              (item.partnerId ?? null) === (validatingJob.partnerId ?? null)
          )
        : [],
    [validatingJob?.stockRegionId, validatingJob?.partnerId, stock]
  );

  useEffect(() => {
    if (validatingJob) {
      // Un dossier non "completed" doit toujours passer par validateJob
      // (qui force status → completed), même s'il contient un rapport partiel (bug historique).
      const isAlreadyCompleted = validatingJob.status === "completed";
      if (
        (validatingJob.stockUsedNeuf != null && Object.keys(validatingJob.stockUsedNeuf).length > 0) ||
        (validatingJob.stockUsedRemplacement != null && Object.keys(validatingJob.stockUsedRemplacement).length > 0)
      ) {
        const neuf: Record<string, number> = {};
        const rem: Record<string, number> = {};
        stockForJob.forEach((item) => {
          neuf[item.id] = validatingJob.stockUsedNeuf?.[item.id] ?? 0;
          rem[item.id] = validatingJob.stockUsedRemplacement?.[item.id] ?? 0;
        });
        setUsageNeuf(neuf);
        setUsageRemplacement(rem);
        setNotes(validatingJob.installerNotes || "");
        setIsEditing(isAlreadyCompleted);
      } else if (validatingJob.stockUsed && Object.keys(validatingJob.stockUsed).length > 0) {
        const neuf: Record<string, number> = {};
        const rem: Record<string, number> = {};
        stockForJob.forEach((item) => {
          const total = validatingJob.stockUsed![item.id] ?? 0;
          neuf[item.id] = total;
          rem[item.id] = 0;
        });
        setUsageNeuf(neuf);
        setUsageRemplacement(rem);
        setNotes(validatingJob.installerNotes || "");
        setIsEditing(isAlreadyCompleted);
      } else {
        const initial: Record<string, number> = {};
        stockForJob.forEach((item) => (initial[item.id] = 0));
        setUsageNeuf(initial);
        setUsageRemplacement(
          stockForJob.reduce((acc, item) => ({ ...acc, [item.id]: 0 }), {})
        );
        setNotes("");
        setIsEditing(false);
      }
    }
  }, [validatingJob, stockForJob]);

  useEffect(() => {
    if (!showValidationSuccess) return;
    const t = setTimeout(() => setShowValidationSuccess(false), 4500);
    return () => clearTimeout(t);
  }, [showValidationSuccess]);

  useEffect(() => {
    if (editingScheduleJob) {
      const d = editingScheduleJob.date || new Date().toISOString().slice(0, 10);
      const timeStr = editingScheduleJob.time || "09:00";
      setEditDate(d);
      setEditTime(timeStr.length === 5 ? timeStr : timeStr.slice(0, 5));
    }
  }, [editingScheduleJob]);

  const openScheduleEdit = (e: React.MouseEvent, job: Job) => {
    e.stopPropagation();
    setEditingScheduleJob(job);
  };

  const handleSaveSchedule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingScheduleJob) return;
    setSavingSchedule(true);
    try {
      const updated = {
        ...editingScheduleJob,
        date: editDate || undefined,
        time: editTime || undefined,
      };
      onUpdateJob(updated);
      if (detailJob?.id === editingScheduleJob.id) setDetailJob(updated);
      setEditingScheduleJob(null);
    } finally {
      setSavingSchedule(false);
    }
  };

  const formatJobDate = (dateStr?: string) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr + "T12:00:00").toLocaleDateString("fr-FR", {
        weekday: "short",
        day: "numeric",
        month: "short",
      });
    } catch {
      return dateStr;
    }
  };

  const handleValidate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatingJob) return;
    const neufToSend: Record<string, number> = {};
    const remToSend: Record<string, number> = {};
    stockForJob.forEach((item) => {
      neufToSend[item.id] = usageNeuf[item.id] ?? 0;
      remToSend[item.id] = usageRemplacement[item.id] ?? 0;
    });
    setSubmitting(true);
    try {
      if (isEditing)
        await onModifyJob(validatingJob.id, neufToSend, remToSend, notes);
      else await onValidateJob(validatingJob.id, neufToSend, remToSend, notes);
      setValidatingJob(null);
      setDetailJob(null);
      setIsEditing(false);
      setShowValidationSuccess(true);
    } catch (err) {
      alert(`Erreur lors de la clôture du chantier.\n\n${err instanceof Error ? err.message : "Erreur inconnue"}`);
    } finally {
      setSubmitting(false);
    }
  };

  const dossiersJobs = jobs.filter(
    (j) =>
      j.status === "scheduled" ||
      j.status === "pending" ||
      j.status === "in_progress" ||
      j.status === "cancelled" ||
      j.status === "completed"
  );
  const historyJobs = jobs.filter((j) => j.status === "completed");

  /** Chantiers en cours (visibles sous le planning, non modifiables par le poseur) */
  const inProgressJobs = useMemo(
    () =>
      jobs
        .filter((j) => j.status === "in_progress")
        .sort((a, b) => {
          const d = (a.date || "").localeCompare(b.date || "");
          if (d !== 0) return d;
          return (a.time || "").localeCompare(b.time || "");
        }),
    [jobs]
  );

  const { t } = useTranslation();
  const viewTitle =
    view === "dossiers"
      ? t("installer.myDossiers")
      : view === "planning"
      ? t("installer.planning")
      : t("installer.history");

  const dossiersFilters = useJobsFilters(dossiersJobs, {
    storageKey: `installer-jobs-filters:${currentUser.id}`,
  });

  const goToDossiersHub = () => {
    dossiersFilters.setField("category", "");
    dossiersFilters.setField("status", "");
    dossiersFilters.setField("dossierType", "");
    setActiveCategory(null);
    setActiveDossierType(null);
    setDossiersScreen("hub");
  };

  const goToDossierTypes = () => {
    dossiersFilters.setField("dossierType", "");
    setActiveDossierType(null);
    setDossiersScreen("types");
  };

  const handleSelectCategory = (category: JobCategory) => {
    dossiersFilters.setField("category", category);
    dossiersFilters.setField("status", "");
    dossiersFilters.setField("dossierType", "");
    dossiersFilters.setField("dateFrom", "");
    dossiersFilters.setField("dateTo", "");
    setActiveCategory(category);
    setActiveDossierType(null);
    if (categoryRequiresDossierType(category)) {
      setDossiersScreen("types");
    } else {
      setDossiersScreen("list");
    }
  };

  const handleSelectDossierType = (type: DossierTypeFilter) => {
    dossiersFilters.setField("dossierType", type);
    setActiveDossierType(type);
    setDossiersScreen("list");
  };

  const toScheduleJobsForTypes = useMemo(
    () => dossiersJobs.filter((j) => jobMatchesCategory(j, "to_schedule")),
    [dossiersJobs]
  );

  const listJobs = view === "dossiers" ? dossiersFilters.filtered : historyJobs;
  const dossiersGrouped = dossiersFilters.grouped;
  const dossiersIsGrouped = view === "dossiers" && dossiersFilters.state.groupBy === "status";

  const renderJobCard = (job: Job) => (
    <div
      key={job.id}
      onClick={() => setDetailJob(job)}
      className="bg-white dark:bg-slate-800 dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 p-5 rounded-2xl shadow-xl dark:shadow-2xl active:scale-[0.98] transition-transform cursor-pointer relative overflow-hidden"
    >
      <div
        className={`absolute left-0 top-0 bottom-0 w-2 ${
          job.status === "cancelled"
            ? "bg-red-500"
            : job.status === "in_progress"
            ? "bg-amber-500"
            : job.status === "scheduled"
            ? "bg-blue-500"
            : "bg-green-500"
        }`}
      />
      <div className="pl-3 flex gap-4">
        <div className="flex-1 min-w-0">
          <div className="mb-2">
            <button
              type="button"
              onClick={(e) => job.status !== "cancelled" && openScheduleEdit(e, job)}
              disabled={job.status === "cancelled"}
              className={`text-left group rounded-xl border-2 border-dashed px-3 py-2 transition-colors min-w-[8rem] ${
                job.status === "cancelled"
                  ? "border-gray-200 dark:border-gray-600 opacity-60 cursor-default"
                  : "border-gray-200 dark:border-gray-600 hover:border-orange-400 dark:hover:border-orange-500 hover:bg-orange-50/50 dark:hover:bg-orange-900/20"
              }`}
              title={job.status === "cancelled" ? "Dossier annulé" : "Modifier la date et l'heure du RDV"}
            >
              <span className="text-xs text-gray-500 dark:text-gray-400 block mb-0.5">{t("installer.date")}</span>
              <span className={`text-sm font-bold block ${job.date ? "text-gray-900 dark:text-white" : "text-gray-400 dark:text-gray-500"} group-hover:text-orange-600 dark:group-hover:text-orange-400`}>
                {job.date ? formatJobDate(job.date) : "—"}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400 block mt-2 mb-0.5">{t("installer.time")}</span>
              <span className={`text-lg font-bold block ${job.time ? "text-gray-900 dark:text-white" : "text-gray-400 dark:text-gray-500"} group-hover:text-orange-600 dark:group-hover:text-orange-400`}>
                {job.time || "—"}
              </span>
            </button>
          </div>
          <h3 className="font-bold text-gray-800 dark:text-gray-100 text-lg leading-tight mb-1">
            {job.clientName}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 text-sm truncate">
            {job.clientAddress}
          </p>
          {job.clientPhone ? (
            <p className="mt-2 flex items-center gap-1.5 text-sm">
              <Phone size={14} className="text-gray-400 dark:text-gray-500 shrink-0" />
              <span className="text-gray-700 dark:text-gray-300 font-medium">{job.clientPhone}</span>
              <a
                href={`tel:${job.clientPhone}`}
                onClick={(e) => e.stopPropagation()}
                className="ml-2 px-2 py-1 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-bold hover:bg-green-200 dark:hover:bg-green-800/40 transition-colors"
              >
                {t("installer.call")}
              </a>
            </p>
          ) : null}
          <div className="mt-3 flex flex-wrap gap-2">
            {getDossierTypeLabel(job.dossierType) && (
              <span className="text-xs font-semibold bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-200 px-2.5 py-1 rounded-full">
                {getDossierTypeLabel(job.dossierType)}
              </span>
            )}
            {getLightingTypeLabel(job.lightingType) && (
              <span className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 px-2.5 py-1 rounded-full">
                {getLightingTypeLabel(job.lightingType)}
              </span>
            )}
            {job.surface && (
              <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded-full">
                {job.surface} m²
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-3 shrink-0">
          {job.status === "scheduled" && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                const updated = { ...job, status: "in_progress" as const };
                onUpdateJob(updated);
              }}
              className="px-3 py-1.5 rounded-full text-xs font-bold tracking-wide bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-300/50 dark:border-amber-500/30 hover:bg-amber-200 dark:hover:bg-amber-800/40 transition-colors flex items-center gap-1.5"
            >
              <Clock size={12} /> Mettre en cours
            </button>
          )}
          <span
            className={`px-3 py-1.5 rounded-full text-xs font-bold tracking-wide ${
              job.status === "cancelled"
                ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                : job.status === "scheduled"
                ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                : job.status === "in_progress"
                ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                : job.status === "pending"
                ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                : "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
            }`}
          >
            {job.status === "scheduled" ? "À FAIRE" : job.status === "in_progress" ? "EN COURS" : job.status === "pending" ? "SANS DATE" : job.status === "cancelled" ? "ANNULÉ" : "TERMINÉ"}
          </span>
          {installerCanSeePartner && (
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500 font-medium mb-0.5">
                Partenaire
              </p>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200 max-w-[10rem] truncate" title={job.partnerId ? (() => { const p = partners.find((x) => x.id === job.partnerId); return p?.company ?? p?.name ?? ""; })() : undefined}>
                {job.partnerId
                  ? (() => {
                      const p = partners.find((x) => x.id === job.partnerId);
                      return p?.company ?? p?.name ?? "Partenaire";
                    })()
                  : "—"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24 md:pb-0">
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200/80 dark:border-white/10 z-30 px-5 py-5 flex justify-between items-center shadow-sm shadow-gray-200/20 dark:shadow-none">
        <div className="flex items-center gap-4 min-w-0">
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            className="p-2.5 -ml-1 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white shrink-0 transition-colors"
            aria-label="Menu"
          >
            <Menu size={26} />
          </button>
          {ownerLogoUrl ? (
            <img src={ownerLogoUrl} alt="" className="w-16 h-16 rounded-2xl object-cover shrink-0 ring-1 ring-gray-200/80 dark:ring-white/10 shadow-sm" />
          ) : null}
          <div className="min-w-0">
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-900 dark:text-white tracking-tight leading-tight truncate" title={ownerCompanyName || undefined}>
              {ownerCompanyName || t("installer.spaceTitle")}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium truncate mt-0.5">
              {viewTitle} · {installerName}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <DarkModeToggle />
          <button
            onClick={onLogout}
            className="p-2.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 rounded-xl transition-colors"
          >
            <LogOut size={22} />
          </button>
        </div>
      </div>

      {showValidationSuccess && (
        <div
          role="status"
          aria-live="polite"
          className="mx-4 mt-3 flex items-center gap-2 rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/30 px-4 py-3 text-green-800 dark:text-green-200 shadow-sm"
        >
          <CheckCircle size={20} className="shrink-0 text-green-600 dark:text-green-400" />
          <div className="min-w-0">
            <p className="font-medium">{t("installer.validationSuccess")}</p>
            <p className="text-sm opacity-90">{t("installer.validationInHistory")}</p>
          </div>
        </div>
      )}

      {menuOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50 dark:bg-black/60 backdrop-blur-sm"
            aria-hidden
            onClick={() => setMenuOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 w-80 max-w-[90vw] bg-white dark:bg-gray-800 border-r border-gray-200/80 dark:border-white/10 shadow-2xl flex flex-col animate-in slide-in-from-left duration-200">
            <div className="p-6 pb-8 border-b border-gray-200/80 dark:border-white/10">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  {ownerLogoUrl ? (
                    <img src={ownerLogoUrl} alt="" className="w-16 h-16 rounded-2xl object-cover shrink-0 ring-1 ring-gray-200/80 dark:ring-white/10 shadow-sm" />
                  ) : (
                    <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-white/10 flex items-center justify-center shrink-0">
                      <Wrench className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight leading-tight truncate">
                      {ownerCompanyName || t("installer.menu")}
                    </h2>
                    {ownerCompanyName ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">{t("installer.spaceTitle")}</p>
                    ) : null}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setMenuOpen(false)}
                  className="p-2.5 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0 transition-colors"
                  aria-label={t("installer.close")}
                >
                  <X size={22} />
                </button>
              </div>
            </div>
            <nav className="p-4 space-y-1.5">
              <button
                type="button"
                onClick={() => { setDetailJob(null); setView("dossiers"); goToDossiersHub(); setMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left font-medium transition-colors ${
                  view === "dossiers"
                    ? "bg-orange-500 text-white"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                <Briefcase size={20} /> {t("installer.myDossiers")}
              </button>
              <button
                type="button"
                onClick={() => { setDetailJob(null); setView("planning"); setMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left font-medium transition-colors ${
                  view === "planning"
                    ? "bg-orange-500 text-white"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                <Calendar size={20} /> {t("installer.planning")}
              </button>
              <button
                type="button"
                onClick={() => { setDetailJob(null); setView("history"); setMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left font-medium transition-colors ${
                  view === "history"
                    ? "bg-orange-500 text-white"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                <History size={20} /> {t("installer.history")}
              </button>
            </nav>
          </div>
        </>
      )}

      {!detailJob && view === "planning" && (
        <div className="px-2 sm:px-4 md:px-8 pb-8 max-w-4xl mx-auto min-w-0 overflow-x-auto space-y-6">
          <div className="overflow-x-auto min-w-0">
            <PlanningCalendar
              jobs={jobs.filter((j) => j.date)}
              onJobSelect={(job) => setDetailJob(job)}
              onUpdateJob={onUpdateJob}
            />
          </div>
          {/* Liste des chantiers en cours (visible, non modifiable) — comme l'admin */}
          <div className="rounded-2xl overflow-hidden bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl border border-gray-200/60 dark:border-white/10 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.02)] dark:shadow-[0_8px_32px_-8px_rgba(0,0,0,0.3)]">
            <div className="p-4 md:p-5 border-b border-gray-100 dark:border-white/5">
              <h3 className="text-[15px] font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Clock size={18} className="text-amber-500" />
                {t("installer.jobsInProgress")}
              </h3>
            </div>
            <div className="p-4 md:p-5">
              {inProgressJobs.length === 0 ? (
                <p className="text-[14px] text-gray-500 dark:text-slate-400">{t("installer.noJobsInProgress")}</p>
              ) : (
                <ul className="space-y-2">
                  {inProgressJobs.map((job) => (
                    <li key={job.id}>
                      <button
                        type="button"
                        onClick={() => setDetailJob(job)}
                        className="w-full text-left flex flex-wrap items-center gap-x-3 gap-y-1 p-3 rounded-xl border border-amber-200/60 dark:border-amber-500/20 bg-amber-50/80 dark:bg-amber-500/10 hover:bg-amber-100/80 dark:hover:bg-amber-500/20 transition-colors cursor-pointer"
                      >
                        <span className="font-medium text-gray-900 dark:text-white truncate">{job.clientName}</span>
                        {job.clientPhone && (
                          <span className="text-[13px] text-gray-600 dark:text-slate-300 flex items-center gap-1">
                            <Phone size={12} /> {job.clientPhone}
                          </span>
                        )}
                        {job.date && (
                          <span className="text-[13px] text-gray-500 dark:text-slate-400">
                            {new Date(job.date + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}
                            {job.time ? ` · ${job.time}` : ""}
                          </span>
                        )}
                        <ChevronRight size={16} className="ml-auto text-gray-400 dark:text-slate-500 shrink-0" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {!detailJob && view !== "planning" && (
        <div className="px-4 max-w-lg mx-auto space-y-4">
          {view === "dossiers" && dossiersScreen === "hub" && (
            <JobsCategoryHub
              jobs={dossiersJobs}
              accent="blue"
              layout="rows"
              onSelect={handleSelectCategory}
            />
          )}
          {view === "dossiers" && dossiersScreen === "types" && activeCategory && (
            <JobsDossierTypeHub
              jobs={toScheduleJobsForTypes}
              category={activeCategory}
              accent="blue"
              layout="rows"
              onSelect={handleSelectDossierType}
              onBack={goToDossiersHub}
            />
          )}
          {view === "dossiers" && dossiersScreen === "list" && (
            <>
              <div className="flex flex-wrap items-center gap-3 px-1">
                <button
                  type="button"
                  onClick={
                    activeCategory && categoryRequiresDossierType(activeCategory)
                      ? goToDossierTypes
                      : goToDossiersHub
                  }
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                >
                  <ChevronLeft size={18} />
                  {activeCategory && categoryRequiresDossierType(activeCategory)
                    ? "Types"
                    : "Catégories"}
                </button>
                <h2 className="text-base font-semibold text-gray-900 dark:text-white tracking-tight flex-1 min-w-0">
                  {activeCategory ? JOB_CATEGORY_LABELS[activeCategory] : t("installer.jobs")}
                  {activeDossierType ? (
                    <span className="text-gray-500 dark:text-slate-400 font-normal">
                      {" "}
                      · {DOSSIER_TYPE_FILTER_LABELS[activeDossierType]}
                    </span>
                  ) : null}
                  <span className="ml-2 text-sm font-normal text-gray-500 dark:text-slate-400 tabular-nums">
                    ({dossiersFilters.filtered.length})
                  </span>
                </h2>
              </div>
              <JobsFiltersBar
                filters={dossiersFilters}
                layout="compact"
                accent="blue"
                showStatusPills={false}
                totalCount={dossiersJobs.length}
              />
            </>
          )}
          {view === "dossiers" && (dossiersScreen === "hub" || dossiersScreen === "types") ? null : listJobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-gray-500">
              <Calendar size={48} className="mb-4 opacity-20" />
              <p>
                {view === "dossiers"
                  ? (dossiersFilters.activeCount > 0 ? "Aucun dossier ne correspond aux filtres" : "Aucun dossier assigné")
                  : "Aucun chantier terminé"}
              </p>
            </div>
          ) : dossiersIsGrouped ? (
            dossiersGrouped.map((group) => (
              <div key={group.status} className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400 px-1 flex items-center gap-2">
                  {STATUS_LABELS[group.status]}
                  <span className="text-[10px] font-bold bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 rounded-full px-2 py-0.5">
                    {group.jobs.length}
                  </span>
                </h3>
                {group.jobs.map((job) => renderJobCard(job))}
              </div>
            ))
          ) : (
            listJobs.map((job) => renderJobCard(job))
          )}
        </div>
      )}

      {detailJob && (
        <div className="px-4 py-2 max-w-lg mx-auto animate-in slide-in-from-right duration-200">
          <button
            onClick={() => setDetailJob(null)}
            className="mb-4 flex items-center gap-2 text-gray-500 dark:text-gray-400 font-medium"
          >
            <ChevronRight className="rotate-180" size={20} /> Retour liste
          </button>
          <div className="bg-white dark:bg-slate-800 dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl shadow-xl dark:shadow-2xl overflow-hidden mb-6">
            <div
              className={`p-6 text-white ${
                detailJob.status === "completed"
                  ? "bg-gray-800 dark:bg-gray-700"
                  : "bg-orange-500 dark:bg-orange-600"
              }`}
            >
              <div className="flex flex-wrap gap-2 mb-3">
                {getDossierTypeLabel(detailJob.dossierType) ? (
                  <span className="text-xs font-bold uppercase tracking-wide bg-white/20 px-3 py-1 rounded-full">
                    {getDossierTypeLabel(detailJob.dossierType)}
                  </span>
                ) : (
                  <p className="opacity-80 text-sm font-medium">INTERVENTION</p>
                )}
                {getLightingTypeLabel(detailJob.lightingType) && (
                  <span className="text-xs font-semibold bg-white/15 px-3 py-1 rounded-full">
                    {getLightingTypeLabel(detailJob.lightingType)}
                  </span>
                )}
              </div>
              {detailJob.status === "in_progress" ? (
                <div className="w-full text-left rounded-xl border-2 border-white/30 bg-white/10 px-4 py-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs opacity-80 mb-1">{t("installer.appointmentDate")}</p>
                      <p className={`text-lg font-bold ${detailJob.date ? "" : "opacity-70"}`}>
                        {detailJob.date ? formatJobDate(detailJob.date) : "Non définie"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs opacity-80 mb-1">{t("installer.appointmentTime")}</p>
                      <p className={`text-lg font-bold ${detailJob.time ? "" : "opacity-70"}`}>
                        {detailJob.time || "Non définie"}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs mt-2 opacity-70">{t("installer.viewOnlyInProgress")}</p>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={(e) => openScheduleEdit(e, detailJob)}
                  className="w-full text-left rounded-xl border-2 border-white/30 bg-white/10 px-4 py-3 hover:bg-white/20 transition-colors"
                  title="Modifier la date et l'heure du RDV"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs opacity-80 mb-1">{t("installer.appointmentDate")}</p>
                      <p className={`text-lg font-bold ${detailJob.date ? "" : "opacity-70"}`}>
                        {detailJob.date ? formatJobDate(detailJob.date) : "Non définie"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs opacity-80 mb-1">{t("installer.appointmentTime")}</p>
                      <p className={`text-lg font-bold ${detailJob.time ? "" : "opacity-70"}`}>
                        {detailJob.time || "Non définie"}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs mt-2 opacity-80">{t("installer.tapToEdit")}</p>
                </button>
              )}
              <p className="text-lg font-medium opacity-90 mt-3">{detailJob.clientName}</p>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    detailJob.clientAddress
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex flex-col items-center justify-center bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 p-4 rounded-2xl active:scale-95 transition-transform"
                >
                  <Navigation size={32} className="mb-2" />
                  <span className="font-bold">{t("installer.imGoing")}</span>
                </a>
                {detailJob.clientPhone ? (
                  <a
                    href={`tel:${detailJob.clientPhone}`}
                    className="flex flex-col items-center justify-center bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 p-4 rounded-2xl active:scale-95 transition-transform"
                  >
                    <Phone size={32} className="mb-2" />
                    <span className="font-bold">{t("installer.call")}</span>
                    <span className="text-xs mt-1 opacity-90 truncate w-full text-center">{detailJob.clientPhone}</span>
                  </a>
                ) : (
                  <div className="flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 p-4 rounded-2xl">
                    <Phone size={32} className="mb-2 opacity-60" />
                    <span className="text-sm font-medium">{t("installer.call")}</span>
                    <span className="text-xs mt-1">{t("installer.noPhone")}</span>
                  </div>
                )}
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl">
                  <MapPin className="text-gray-400 dark:text-gray-500 mt-1 shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400 dark:text-gray-500 uppercase font-bold">
                      ADRESSE
                    </p>
                    <p className="text-gray-900 dark:text-gray-100 font-medium leading-relaxed">
                      {detailJob.clientAddress}
                    </p>
                  </div>
                </div>
                {installerCanSeePartner && (
                  <div className="rounded-2xl border border-slate-200 dark:border-slate-600/50 overflow-hidden bg-slate-50 dark:bg-slate-700/50">
                    <button
                      type="button"
                      onClick={() => setPartnerSectionOpen((o) => !o)}
                      className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-slate-100/50 dark:hover:bg-slate-600/30 transition-colors"
                    >
                      <span className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <Briefcase size={18} className="text-slate-500 dark:text-slate-400 shrink-0" />
                        Partenaire (apporteur)
                      </span>
                      {partnerSectionOpen ? (
                        <ChevronDown size={20} className="text-slate-500 dark:text-slate-400 shrink-0" />
                      ) : (
                        <ChevronRight size={20} className="text-slate-500 dark:text-slate-400 shrink-0" />
                      )}
                    </button>
                    {partnerSectionOpen && (
                      <div className="px-4 pb-4 pt-0">
                        <p className="text-gray-900 dark:text-gray-100 font-medium">
                          {detailJob.partnerId
                            ? (() => {
                                const p = partners.find((x) => x.id === detailJob.partnerId);
                                return p?.company ?? p?.name ?? "Partenaire";
                              })()
                            : "—"}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                {(getDossierTypeLabel(detailJob.dossierType) || getLightingTypeLabel(detailJob.lightingType)) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {getDossierTypeLabel(detailJob.dossierType) && (
                      <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-2xl">
                        <p className="text-xs text-indigo-600 dark:text-indigo-400 uppercase font-bold mb-1">
                          TYPE DE DOSSIER
                        </p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                          {getDossierTypeLabel(detailJob.dossierType)}
                        </p>
                      </div>
                    )}
                    {getLightingTypeLabel(detailJob.lightingType) && (
                      <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-2xl">
                        <p className="text-xs text-orange-600 dark:text-orange-400 uppercase font-bold mb-1">
                          TYPE D&apos;INTERVENTION
                        </p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                          {getLightingTypeLabel(detailJob.lightingType)}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl">
                    <p className="text-xs text-gray-400 dark:text-gray-500 uppercase font-bold mb-1">
                      HAUTEUR
                    </p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">
                      {detailJob.ceilingHeight || "-"}
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl">
                    <p className="text-xs text-gray-400 dark:text-gray-500 uppercase font-bold mb-1">
                      SURFACE
                    </p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">
                      {detailJob.surface || "-"} m²
                    </p>
                  </div>
                  {detailJob.estimatedLedCount && (
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl col-span-2">
                      <p className="text-xs text-blue-600 dark:text-blue-400 uppercase font-bold mb-1">
                        LED ESTIMÉES
                      </p>
                      <p className="text-xl font-bold text-gray-900 dark:text-white">
                        {detailJob.estimatedLedCount}
                      </p>
                    </div>
                  )}
                  {(detailJob.dehumidifierCount != null || detailJob.timerCount != null || detailJob.needsCounter || detailJob.needsGenerator) && (
                    <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-2xl col-span-2">
                      <p className="text-xs text-indigo-600 dark:text-indigo-400 uppercase font-bold mb-2">
                        BRIEF TECHNIQUE (INDICATIF)
                      </p>
                      <div className="space-y-1 text-sm text-gray-900 dark:text-white">
                        {detailJob.dehumidifierCount != null && <p>Déshumidificateurs: <strong>{detailJob.dehumidifierCount}</strong></p>}
                        {detailJob.timerCount != null && <p>Minuteries TH 122: <strong>{detailJob.timerCount}</strong></p>}
                        {(detailJob.needsCounter || detailJob.needsGenerator) && (
                          <p>
                            Options:{" "}
                            {[detailJob.needsCounter ? "Compteur" : null, detailJob.needsGenerator ? "Générateur" : null]
                              .filter(Boolean)
                              .join(" / ")}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {detailJob.notes && (
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-800 rounded-2xl">
                    <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400 font-bold mb-2">
                      <AlertCircle size={18} /> NOTE BUREAU
                    </div>
                    <p className="text-yellow-800 dark:text-yellow-200">{detailJob.notes}</p>
                  </div>
                )}
                <DossierNotes dossierId={detailJob.id} currentUser={currentUser} />
                {detailJob.signedQuoteUrls && detailJob.signedQuoteUrls.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{t("signedQuote.title")}</p>
                    {detailJob.signedQuoteUrls.map((path) => (
                      <SignedQuoteViewer
                        key={path}
                        jobId={detailJob.id}
                        storagePath={path}
                        canDelete={false}
                      />
                    ))}
                  </div>
                )}
                {detailJob.stockToPrepare && Object.keys(detailJob.stockToPrepare).length > 0 && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-2xl">
                    <div className="flex items-center gap-2 text-blue-700 dark:text-blue-400 font-bold mb-3">
                      <Package size={18} /> MATÉRIEL À PRÉPARER
                    </div>
                    <ul className="space-y-2 text-blue-800 dark:text-blue-200">
                      {Object.entries(detailJob.stockToPrepare).map(([itemId, qty]) => {
                        if (qty == null || Number(qty) <= 0) return null;
                        const name = stock.find((s) => s.id === itemId)?.name ?? itemId;
                        return (
                          <li key={itemId} className="flex justify-between items-center font-medium">
                            <span>{name}</span>
                            <span className="font-bold">{qty}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
              {detailJob.status === "scheduled" && (
                <button
                  type="button"
                  onClick={() => {
                    const updated = { ...detailJob, status: "in_progress" as const };
                    onUpdateJob(updated);
                    setDetailJob(updated);
                  }}
                  className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-2xl active:scale-[0.98] transition-all flex items-center justify-center gap-2 mb-3"
                >
                  <Clock size={18} /> METTRE EN COURS
                </button>
              )}
              {detailJob.status === "scheduled" && (
                <button
                  onClick={() => setValidatingJob(detailJob)}
                  className="w-full py-4 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 font-bold rounded-2xl shadow-xl shadow-gray-200 dark:shadow-gray-800 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                >
                  <CheckCircle /> CLÔTURER LE CHANTIER
                </button>
              )}
              {detailJob.status === "in_progress" && (
                <p className="text-sm text-gray-500 dark:text-slate-400 text-center py-2">{t("installer.viewOnlyInProgress")}</p>
              )}
              {detailJob.status === "completed" && (
                <button
                  onClick={() => setValidatingJob(detailJob)}
                  className="w-full py-4 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-bold rounded-2xl active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                >
                  <RotateCcw size={18} /> MODIFIER LE RAPPORT
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <Modal isOpen={!!validatingJob} onClose={() => setValidatingJob(null)}>
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isEditing ? "Modifier le Rapport" : "Rapport de Fin"}
          </h3>
          <p className="text-gray-500 dark:text-gray-400">{t("installer.materialUsed")}</p>
        </div>

        <form onSubmit={handleValidate} className="space-y-6">
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-2xl space-y-4 max-h-80 overflow-y-auto">
            {validatingJob && stockForJob.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Aucun article de stock pour ce partenaire dans cette région.
              </p>
            ) : (
            stockForJob.map((item) => {
              const qtyNeuf = usageNeuf[item.id] ?? 0;
              const qtyRem = usageRemplacement[item.id] ?? 0;
              const total = qtyNeuf + qtyRem;
              return (
                <div key={item.id} className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 shrink-0">
                      {item.name}
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex flex-col items-center">
                        <span className="text-xs text-gray-500 dark:text-gray-400">{t("installer.new")}</span>
                        <input
                          type="number"
                          min={0}
                          value={qtyNeuf}
                          onChange={(e) =>
                            setUsageNeuf((u) => ({
                              ...u,
                              [item.id]: Math.max(0, parseInt(e.target.value, 10) || 0),
                            }))
                          }
                          className="w-14 px-2 py-1.5 border dark:border-gray-600 dark:bg-gray-800 rounded-lg text-center text-gray-900 dark:text-white"
                        />
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="text-xs text-gray-500 dark:text-gray-400">{t("installer.replacement")}</span>
                        <input
                          type="number"
                          min={0}
                          value={qtyRem}
                          onChange={(e) =>
                            setUsageRemplacement((u) => ({
                              ...u,
                              [item.id]: Math.max(0, parseInt(e.target.value, 10) || 0),
                            }))
                          }
                          className="w-14 px-2 py-1.5 border dark:border-gray-600 dark:bg-gray-800 rounded-lg text-center text-gray-900 dark:text-white"
                        />
                      </div>
                      {total > 0 && (
                        <span className="text-xs text-gray-400 dark:text-gray-500 pt-2">
                          Total: {total}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
            )}
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">
              COMMENTAIRE TECHNIQUE
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-4 py-3 border dark:border-gray-600 dark:bg-gray-800 rounded-xl h-24 focus:bg-white dark:focus:bg-gray-800 transition-colors text-gray-900 dark:text-white placeholder-gray-500"
              placeholder="RAS, tout s'est bien passé..."
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-2xl font-bold text-lg shadow-lg shadow-green-200 dark:shadow-green-900/30 active:scale-[0.98] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {submitting ? "Envoi…" : isEditing ? "METTRE À JOUR" : "VALIDER ET FERMER"}
          </button>
        </form>
      </Modal>

      <Modal isOpen={!!editingScheduleJob} onClose={() => setEditingScheduleJob(null)}>
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar size={32} />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
            Modifier le RDV
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            {editingScheduleJob?.clientName}
          </p>
        </div>
        <form onSubmit={handleSaveSchedule} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase mb-2">
                Date
              </label>
              <input
                type="date"
                lang={getCurrentLocale() === "fr" ? "fr-FR" : getCurrentLocale() === "es" ? "es-ES" : "en-US"}
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
                className="w-full px-4 py-3 border dark:border-gray-600 dark:bg-gray-800 rounded-xl text-gray-900 dark:text-white"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase mb-2">
                Heure
              </label>
              <input
                type="time"
                value={editTime}
                onChange={(e) => setEditTime(e.target.value)}
                className="w-full px-4 py-3 border dark:border-gray-600 dark:bg-gray-800 rounded-xl text-gray-900 dark:text-white"
                required
              />
            </div>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setEditingScheduleJob(null)}
              className="flex-1 py-3 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={savingSchedule}
              className="flex-1 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-bold shadow-lg disabled:opacity-60"
            >
              {savingSchedule ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
